import myTraderaItemActions from '../state/actions/my-tradera-items';
import { openModal as openMyTraderaModal } from 'tradera/apps/my-tradera/app/state/reducers/my-tradera';
import purchaseOrderActions from '../state/actions/purchase-order';
import featureToggles from '../utils/feature-toggles';
import { getMessagingThreadLink } from './messaging-helpers';
import { openMessagingModal, openModal } from 'tradera/state/ui/reducer';
import { SINGLE_FEEDBACK_DIALOG_ID } from 'tradera/apps/my-tradera/app/components/leave-feedback/constants';
import { isNormalListViewType } from './item-helper';
import { showSuccessToast } from 'tradera/services/toasts';
import { PaidStatuses } from 'tradera/constants/paid-statuses';

export const types = {
    LINK: 'LINK',
    EXTERNAL_LINK: 'EXTERNAL_LINK',
    DISPATCH: 'DISPATCH'
};

const getId = (item) => item.key;
const getIds = (item) => [item.itemId];

const fixItemUrlIfNeeded = (item = {}) => {
    // Fix this at backend?
    return item.itemUrl && item.itemUrl[0] !== '/'
        ? `/${item.itemUrl}`
        : item.itemUrl;
};

const showModal = (translationKey) => {
    return openMyTraderaModal({
        bulkAction: 'MytListInfo',
        modal: {
            cssClass: 'fixed',
            icon: 'check',
            isSingleButton: true,
            getTranslatedMessage: (t) => t(translationKey)
        }
    });
};

// isPrimaryAction = will be primary action unless one before has not already been set as primary action
// Hence, the order of the actions matter.
const commonActions = (item) => {
    const actions = {
        itemsFromSeller: () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Säljarens annonser',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_itemsFromSeller'),
            url: `/profile/items/${item.seller.id}/${item.seller.cleanedAlias}`,
            gaLabel: 'SellerOtherItems',
            isVisible: () => !item.haveMultipleRows,
            isPrimaryAction: () => true
        })
    };
    return actions;
};

export const activeBuyerActions = (item, listViewType, memberId) => {
    const common = commonActions(item);
    const actions = [
        () => {
            const isLeading =
                item.bidInformation &&
                item.bidInformation.leadingBidder &&
                item.bidInformation.leadingBidder.id === memberId;
            const fixedItemUrl = fixItemUrlIfNeeded(item);
            return {
                id: getId(item),
                type: types.LINK,
                label: isLeading ? 'Öka maxbud' : 'Lägg bud',
                getTranslatedLabel: (t) =>
                    isLeading
                        ? t('myt_itemCard_actions_raiseMaxBid')
                        : t('myt_itemCard_actions_placeBid'),
                url: fixedItemUrl,
                gaLabel: isLeading ? 'RaiseMaxBid' : 'PlaceBid',
                buttonTheme: 'success',
                isVisible: () => true,
                isPrimaryAction: () => true
            };
        },
        common.itemsFromSeller,
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Dölj',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_hide'),
            dispatch: (dispatch) => {
                dispatch(
                    myTraderaItemActions.buyerActiveItemsHideItems(
                        getIds(item),
                        listViewType
                    )
                );
            },
            gaLabel: 'HideBuyerActiveItem',
            isVisible: () =>
                (item.caller.leadingBidRemoved ||
                    (item.bidInformation.leadingBidder &&
                        item.bidInformation.leadingBidder.id !== memberId)) &&
                item.caller.hidden === false,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Visa',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_show'),
            dispatch: (dispatch) => {
                dispatch(
                    myTraderaItemActions.buyerActiveItemsShowItems(
                        getIds(item),
                        listViewType
                    )
                );
            },
            gaLabel: 'ShowBuyerActiveItem',
            isVisible: () => item.caller.hidden === true, // User should always be able to Show a hidden item, no matter if leading or not
            isPrimaryAction: () => true
        })
    ];
    return actions;
};

export const boughtActions = (item, listViewType, memberId) => {
    const common = commonActions(item);
    const actions = [
        () => ({
            id: getId(item),
            type:
                item.transaction.paidStatus === PaidStatuses.EXPIRED
                    ? types.DISPATCH
                    : types.LINK,
            label: 'Betala',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_pay'),
            dispatch: (dispatch) => {
                dispatch(showModal('myt_orderinfo_paymentExpired'));
            },
            url: `/Shopping/Checkout/SelectPayment?purchaseOrderId=${item.transaction.purchaseOrderId}`,
            gaLabel: 'Pay',
            buttonTheme: 'success',
            isVisible: () =>
                !item.transaction.buyerState.paid && !item.haveMultipleRows,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Markera som betald',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_markAsPaid'),
            dispatch: (dispatch) => {
                if (item.transaction.paidStatus === PaidStatuses.EXPIRED) {
                    return dispatch(showModal('myt_orderinfo_paymentExpired'));
                }
                showSuccessToast('siteWideNotifications_orderMarkedAsPaid');
                dispatch(
                    myTraderaItemActions.markPurchaseOrdersAsPaidByBuyer(
                        [item.transaction.purchaseOrderId],
                        listViewType
                    )
                );
            },
            gaLabel: 'MarkAsPaid',
            isVisible: () =>
                item.transaction.paidStatus === PaidStatuses.UNPAID &&
                !item.haveMultipleRows,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Avmarkera som betald',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_unmarkAsPaid'),
            dispatch: (dispatch) =>
                dispatch(
                    myTraderaItemActions.unmarkPurchaseOrdersAsPaidByBuyer(
                        [item.transaction.purchaseOrderId],
                        listViewType
                    )
                ),
            gaLabel: 'UnmarkAsPaid',
            isVisible: () =>
                item.transaction.paidStatus ===
                    PaidStatuses.MARKED_AS_PAID_BY_BUYER &&
                !item.haveMultipleRows,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Lämna omdöme',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_leaveFeedback'),
            dispatch: (dispatch) => {
                dispatch(
                    openModal({
                        modalId: SINGLE_FEEDBACK_DIALOG_ID,
                        data: {
                            transactionId: item.transaction.transactionId
                        }
                    })
                );
            },
            gaLabel: 'LeaveFeedbackOnSeller',
            isVisible: () =>
                !item.transaction.buyerState.feedbackLeft &&
                !item.haveMultipleRows &&
                item.transaction.paidStatus !== PaidStatuses.PAYMENT_PENDING,
            isPrimaryAction: () => !item.haveMultipleRows
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Orderinformation',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_orderInformation'),
            url: `/my/order/${item.transaction.purchaseOrderId}`,
            gaLabel: 'OrderInformation',
            isVisible: () => !item.haveMultipleRows,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Kontakta säljare',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_contactSeller'),
            dispatch: (dispatch) => {
                const src = getMessagingThreadLink(
                    item.seller.id,
                    memberId,
                    item.itemId,
                    {
                        traderaUserId: item.seller.id,
                        singleThread: true,
                        openForm: true,
                        source: 'contact_seller'
                    }
                );
                const title = 'Kontakta säljare';
                const recipientId = item.seller.id;
                dispatch(
                    openMessagingModal({
                        title,
                        src,
                        recipientId,
                        item
                    })
                );
            },
            gaLabel: 'ContactSeller',
            isVisible: () =>
                featureToggles.isMessagingEnabled && !item.haveMultipleRows,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Spåra paket',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_trackShipment'),
            url: `/my/order/${item.transaction.purchaseOrderId}#shipping`,
            gaLabel: 'BuyerTrackShipment',
            isVisible: () =>
                !item.haveMultipleRows && !!item.transaction.trackingLink,
            isPrimaryAction: () => true
        }),
        common.itemsFromSeller,
        () => ({
            // "INR" = "Item Not Received"
            id: getId(item),
            type: types.LINK,
            label: 'Anmäl utebliven vara',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_reportItemNotReceived'),
            url: `/resolution/INR2/${item.transaction.purchaseOrderId}`,
            gaLabel: 'ReportItemNotReceived',
            isVisible: () =>
                !item.transaction.hasBuyerProtection &&
                !item.transaction.buyerState.inr &&
                [
                    PaidStatuses.MARKED_AS_PAID_BY_BUYER,
                    PaidStatuses.MARKED_AS_PAID_BY_SELLER,
                    PaidStatuses.PAID
                ].includes(item.transaction.paidStatus),
            isPrimaryAction: () => !item.haveMultipleRows
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Anmäl utebliven vara',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_createDispute'),
            url: `/resolution/disputes/${item.transaction.transactionId}`,
            gaLabel: 'CreateDispute',
            isVisible: () =>
                item.transaction.hasBuyerProtection &&
                !item.transaction.dispute &&
                [
                    PaidStatuses.MARKED_AS_PAID_BY_BUYER,
                    PaidStatuses.MARKED_AS_PAID_BY_SELLER,
                    PaidStatuses.PAID
                ].includes(item.transaction.paidStatus),
            isPrimaryAction: () => !item.haveMultipleRows
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Ångra dölj',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_unhide'),
            dispatch: (dispatch) => {
                dispatch(
                    isNormalListViewType(listViewType)
                        ? purchaseOrderActions.updatePurchaseOrderHiddenForBuyer(
                              item.transaction.purchaseOrderId,
                              [item.transaction.transactionId],
                              false,
                              listViewType
                          )
                        : myTraderaItemActions.buyerUnhideTransaction(
                              [item.transaction.transactionId],
                              listViewType
                          )
                );
            },
            gaLabel: 'UnhideSold',
            isVisible: () =>
                isNormalListViewType(listViewType)
                    ? !item.haveMultipleRows &&
                      item.transaction?.buyerState.hidden
                    : item.transaction.buyerState.hidden,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Dölj',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_hide'),
            dispatch: (dispatch) => {
                dispatch(
                    isNormalListViewType(listViewType)
                        ? purchaseOrderActions.updatePurchaseOrderHiddenForBuyer(
                              item.transaction.purchaseOrderId,
                              [item.transaction.transactionId],
                              true,
                              listViewType
                          )
                        : myTraderaItemActions.buyerHideTransaction(
                              [item.transaction.transactionId],
                              listViewType
                          )
                );
            },
            gaLabel: 'HideSold',
            isVisible: () =>
                isNormalListViewType(listViewType)
                    ? !item.haveMultipleRows &&
                      !item.transaction?.buyerState.hidden
                    : !item.transaction.buyerState.hidden,
            isPrimaryAction: () => true
        })
    ];
    return actions;
};

export const lostActions = (item, listViewType) => {
    const common = commonActions(item);
    const actions = [
        common.itemsFromSeller,
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Dölj',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_hide'),
            dispatch: (dispatch) => {
                dispatch(
                    myTraderaItemActions.buyerActiveItemsHideItems(
                        getIds(item),
                        listViewType
                    )
                );
            },
            gaLabel: 'HideBuyerLostItem',
            isVisible: () => true,
            isPrimaryAction: () => true
        })
    ];
    return actions;
};
